/**
 * lg-pdp-express.js — Express Production toggle for the Product Detail Page
 *
 * Behaviour:
 *   - When Express is not yet in cart, the toggle is visual until add-to-cart.
 *   - When the customer clicks "Add to cart" with the toggle ON, this script
 *     silently adds the express variant to cart first, then lets Dawn's native
 *     product-form submit handler fire — so the drawer, cart notification,
 *     and badge all update exactly as they would for a normal add.
 *   - When Express is already active for the order, clicking the PDP toggle
 *     does not change the order. The PDP card stays in an active informational
 *     state and the customer must go to the cart to remove Express.
 */
(function () {
  'use strict';

  function cartString(key, fallback) {
    var strings = window.lostgearStrings && window.lostgearStrings.cart;
    return strings && strings[key] ? strings[key] : fallback;
  }

  var expressEl      = null;
  var formEl         = null;
  var expressBtn     = null;
  var detailEl       = null;
  var statusEl       = null;
  var defaultDetail  = '';
  var activeDetail   = cartString('expressActiveDetail', 'Express Production is already active for this order.');
  var blockedDetail  = cartString('expressBlockedDetail', 'Express Production is not available while a product marked as ineligible for Express is in your cart. Remove the blocking product in the cart to enable Express.');
  var expressLocked  = false;
  var expressBlocked = false;
  var _skipIntercept = false; // lets us re-fire submit without re-intercepting

  // ── Init ─────────────────────────────────────────────────────────────────

  function init() {
    expressEl = document.querySelector('[data-pdp-express]');
    if (!expressEl) return;

    formEl = document.querySelector('[data-type="add-to-cart-form"]');
    if (!formEl) return;

    expressBtn = expressEl.querySelector('[data-pdp-express-btn]');
    detailEl = expressEl.querySelector('[data-pdp-express-detail]');
    statusEl = expressEl.querySelector('[data-pdp-express-status]');
    defaultDetail = detailEl ? detailEl.textContent.trim() : '';
    if (expressEl.dataset.expressActiveDetail) {
      activeDetail = expressEl.dataset.expressActiveDetail;
    }
    if (expressEl.dataset.expressBlockedDetail) {
      blockedDetail = expressEl.dataset.expressBlockedDetail;
    }

    // Toggle button — visual only, no API calls
    if (expressBtn && !expressBtn._lgPdpBound) {
      expressBtn._lgPdpBound = true;
      expressBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (expressEl.classList.contains('lg-express--loading')) return;
        if (expressLocked || expressBlocked) return;
        toggleVisual();
      });
    }

    // Capture-phase listener so we run before Dawn's submit handler
    formEl.addEventListener('submit', onFormSubmit, true);

    syncWithCart();
  }

  // ── Visual toggle (no API) ────────────────────────────────────────────────

  function toggleVisual() {
    var isActive = expressEl.classList.contains('lg-express--active');
    expressEl.classList.remove('lg-express--active', 'lg-express--idle');
    expressEl.classList.add(isActive ? 'lg-express--idle' : 'lg-express--active');
    if (expressBtn) expressBtn.setAttribute('aria-pressed', isActive ? 'false' : 'true');

    var shimmer = expressEl.querySelector('.lg-express__shimmer');
    if (!isActive) {
      // Switching to active — remove shimmer
      if (shimmer) shimmer.remove();
    } else {
      // Switching to idle — restore shimmer
      if (!shimmer) {
        var s = document.createElement('div');
        s.className = 'lg-express__shimmer';
        expressEl.insertBefore(s, expressEl.firstChild);
      }
    }
  }

  function setVisualState(isActive) {
    if (!expressEl) return;

    expressEl.classList.remove('lg-express--active', 'lg-express--idle');
    expressEl.classList.add(isActive ? 'lg-express--active' : 'lg-express--idle');
    if (expressBtn) expressBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    var shimmer = expressEl.querySelector('.lg-express__shimmer');
    if (isActive) {
      if (shimmer) shimmer.remove();
    } else if (!shimmer) {
      var s = document.createElement('div');
      s.className = 'lg-express__shimmer';
      expressEl.insertBefore(s, expressEl.firstChild);
    }
  }

  function setExpressLocked(isLocked) {
    expressLocked = isLocked;
    expressBlocked = false;
    setVisualState(isLocked);
    expressEl.classList.toggle('lg-pdp-express--locked', isLocked);
    expressEl.classList.remove('lg-pdp-express--blocked');

    if (detailEl) {
      detailEl.textContent = isLocked ? activeDetail : defaultDetail;
    }
    if (statusEl) {
      statusEl.textContent = cartString('alreadyInCart', 'Already in cart');
    }

    if (expressBtn) {
      expressBtn.setAttribute('aria-pressed', isLocked ? 'true' : 'false');
      expressBtn.setAttribute('aria-disabled', isLocked ? 'true' : 'false');
      expressBtn.dataset.expressLocked = isLocked ? 'true' : 'false';
    }
  }

  function setExpressBlocked(isBlocked) {
    expressBlocked = isBlocked;
    expressLocked = false;
    setVisualState(false);
    expressEl.classList.toggle('lg-pdp-express--locked', isBlocked);
    expressEl.classList.toggle('lg-pdp-express--blocked', isBlocked);

    if (detailEl) {
      detailEl.textContent = isBlocked ? blockedDetail : defaultDetail;
    }
    if (statusEl) {
      statusEl.textContent = cartString('unavailable', 'Unavailable');
    }

    if (expressBtn) {
      expressBtn.setAttribute('aria-pressed', 'false');
      expressBtn.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
      expressBtn.dataset.expressLocked = isBlocked ? 'true' : 'false';
    }
  }

  function getExpressId() {
    if (!expressEl) return 0;
    return parseInt(expressEl.dataset.expressVariant, 10) || 0;
  }

  function findExpressLine(cart, expressId) {
    if (!cart || !Array.isArray(cart.items) || !expressId) return 0;

    for (var i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      var variantId = parseInt(item.variant_id || item.id, 10);
      if (variantId === expressId && Number(item.quantity || 0) > 0) {
        return i + 1;
      }
    }

    return 0;
  }

  function cartHasExpress(cart, expressId) {
    return findExpressLine(cart, expressId) > 0;
  }

  function fetchCartState() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart fetch failed');
      return r.json();
    });
  }

  function getSharedExpressState() {
    return document.querySelector('[data-cart-drawer-express-state]');
  }

  function syncWithCart() {
    var expressId = getExpressId();
    if (!expressId) return;

    var sharedState = getSharedExpressState();
    if (sharedState && sharedState.dataset.hasBlocker === 'true') {
      setExpressBlocked(true);
      return;
    }
    if (sharedState && sharedState.dataset.expressInCart === 'true') {
      setExpressLocked(true);
      return;
    }

    fetchCartState()
      .then(function (cart) {
        if (cartHasExpress(cart, expressId)) {
          setExpressLocked(true);
        }
      })
      .catch(function (err) {
        console.error('lg-pdp-express cart sync error', err);
      });
  }

  // ── Form submit intercept ────────────────────────────────────────────────

  function onFormSubmit(e) {
    // Pass through if we re-fired the event ourselves, or if express is off
    if (_skipIntercept) return;
    if (!expressEl || !expressEl.classList.contains('lg-express--active')) return;
    if (expressLocked || expressBlocked) return;

    // Take over this submit — add express first, then hand back to Dawn
    e.preventDefault();
    e.stopImmediatePropagation();

    var expressId = getExpressId();
    if (!expressId) return;

    // Show loading state while we add the express variant silently
    var submitBtn  = formEl.querySelector('button[type="submit"][name="add"]');
    var submitSpan = submitBtn ? submitBtn.querySelector('span') : null;
    var origText   = submitSpan ? submitSpan.textContent.trim() : '';

    if (submitBtn)  submitBtn.disabled = true;
    if (submitSpan) submitSpan.textContent = cartString('adding', 'Adding…');

    fetchCartState()
    .then(function (cart) {
      var sharedState = getSharedExpressState();
      if (sharedState && sharedState.dataset.hasBlocker === 'true') {
        setExpressBlocked(true);
        if (submitBtn) submitBtn.disabled = false;
        if (submitSpan) submitSpan.textContent = origText;
        showError(cartString('expressCartError', 'Express Production is unavailable while a product marked as ineligible for Express is in your cart.'));
        return null;
      }

      if (cartHasExpress(cart, expressId)) {
        setExpressLocked(true);
        if (submitBtn) submitBtn.disabled = false;
        if (submitSpan) submitSpan.textContent = origText;

        _skipIntercept = true;
        formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        _skipIntercept = false;
        return null;
      }

      return fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: expressId, quantity: 1 })
      }).then(function (r) { return r.json(); });
    })
    .then(function (data) {
      if (!data) return;
      if (data.status) {
        // Express add failed — restore button and show error
        console.error('lg-pdp-express silent add error', data.status, data.description);
        if (submitBtn)  submitBtn.disabled  = false;
        if (submitSpan) submitSpan.textContent = origText;
        showError(data.description || cartString('expressAddError', 'Could not add Express Production. Please try again.'));
        return;
      }

      // Express is in cart — restore button text and re-fire the submit
      // so Dawn's product-form handler runs and handles all UI (drawer / badge)
      if (submitBtn)  submitBtn.disabled  = false;
      if (submitSpan) submitSpan.textContent = origText;

      _skipIntercept = true;
      formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      _skipIntercept = false;
    })
    .catch(function (err) {
      console.error('lg-pdp-express submit error', err);
      if (submitBtn)  submitBtn.disabled  = false;
      if (submitSpan) submitSpan.textContent = origText;
    });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  function showError(msg) {
    var wrap  = document.querySelector('.product-form__error-message-wrapper');
    var msgEl = wrap ? wrap.querySelector('.product-form__error-message') : null;
    if (!wrap || !msgEl) return;
    msgEl.textContent = msg;
    wrap.hidden = false;
  }

  // ── Boot ─────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
